import { useCurrentWorldContext } from "../../context/currentWorld";

export interface AllWorldRoutes {
  landing: string,
  agreements: {
    basePath: string,
    eula: string,
    dpa: string
  },
  batteryEssentials: {
    overview: string,
    battery: string,
    devices: string
  },
  batteryWarranty: string,
  bulkActions: string,
  core: {
    overview: string,
    devices: string,
    devicesList: string,
    devicesMap: string,
    deviceOverview: string,
    devicePerformance: string,
    performance: string,
    performanceDevices: string,
    anomalies: string,
    anomalyHistory: string,
    anomalyDevices: string,
    applicationsList: string,
    applicationPerformance: string,
    applicationDevicesList: string,
    processesList: string,
    processesCrashes: string,
    processPerformance: string,
    maps: string,
    accessPoints: string,
    accessPointPerformance: string,
    anomalyDetectionBeta: string,
    deviceAnomalyDetails: string,
    deviceAnomalyExamination: string,
    batteries: string,
    batteriesList: string,
    batteriesDevices: string
  },
  settings: {
    basePath: string,
    alarms: string,
    alerts: string,
    anomalies: string,
    devices: string,
    platform: string
  },
  userManagement: string,
  homeLocationManagement: string,
  zones: {
    list: string,
    new: string,
    edit: string
  },
  enrollment: string
}

const useRouteWithWorldId = (pathWithLeadingSlash: string) => {
  const { id: worldId } = useCurrentWorldContext();

  return `/${worldId}${pathWithLeadingSlash}`;
};

export const useBaseWorldRoutes = (): AllWorldRoutes => {
  return {
    landing: '/',
    agreements: {
      basePath: '/agreements',
      eula: '/agreements/eula',
      dpa: '/agreements/dpa',
    },
    // Kept as the literal legacy /battery paths - world-id-route.tsx still mounts BatteryRoutes
    // (now a set of redirects into /core/batteries/*, see battery-route.tsx) here. Link-building
    // consumers use useWorldRoutes().batteryEssentials.* below, which points at the hub directly.
    batteryEssentials: {
      overview: '/battery/overview',
      battery: '/battery',
      devices: '/battery/devices',
    },
    batteryWarranty: '/battery-warranty',
    bulkActions: '/bulkActions',
    core: {
      overview: '/core',
      devices: '/core/devices',
      devicesList: '/core/devices/list',
      devicesMap: '/core/devices/map',
      deviceOverview: '/core/devices/info',
      devicePerformance: '/core/devices/performance',
      performance: '/core/performance',
      performanceDevices: '/core/performance/devices',
      anomalies: '/core/anomalies',
      anomalyHistory: '/core/anomalies/details',
      anomalyDevices: '/core/anomalies/details/devices',
      applicationsList: '/core/applications',
      applicationPerformance: '/core/applications/performance',
      applicationDevicesList: '/core/applications/devices',
      maps: '/core/maps',
      processesList: '/core/processes',
      processesCrashes: '/core/processes/crashes',
      processPerformance: '/core/processes/performance',
      accessPoints: '/core/accessPoints',
      accessPointPerformance: '/core/accessPoints/performance',
      anomalyDetectionBeta: '/core/anomalies-beta',
      deviceAnomalyDetails: '/core/anomalies-beta/details',
      deviceAnomalyExamination: '/core/anomalies-beta/details/examination',
      batteries: '/core/batteries',
      batteriesList: '/core/batteries/list',
      batteriesDevices: '/core/batteries/devices'
    },
    settings: {
      basePath: '/settings',
      alarms: '/settings/alarms',
      alerts: '/settings/alerts',
      anomalies: '/settings/anomalies',
      devices: '/settings/devices',
      platform: '/settings/platform'
    },
    userManagement: '/users',
    homeLocationManagement: '/homeLocations',
    zones: {
      list: '/zones',
      new: '/zones/new',
      edit: '/zones/edit'
    },
    enrollment: '/enrollment'
  };
};

export const useWorldRoutes = (): AllWorldRoutes => {
  return {
    landing: useRouteWithWorldId(useBaseWorldRoutes().landing),
    agreements: {
      basePath: useRouteWithWorldId(useBaseWorldRoutes().agreements.basePath),
      eula: useRouteWithWorldId(useBaseWorldRoutes().agreements.eula),
      dpa: useRouteWithWorldId(useBaseWorldRoutes().agreements.dpa)
    },
    // See the comment on useBaseWorldRoutes() above - these intentionally resolve to /core/batteries/*
    // rather than the literal (still-/battery-based) useBaseWorldRoutes() values.
    batteryEssentials: {
      overview: useRouteWithWorldId(useBaseWorldRoutes().core.batteries),
      battery: useRouteWithWorldId(useBaseWorldRoutes().core.batteriesList),
      devices: useRouteWithWorldId(useBaseWorldRoutes().core.batteriesDevices),
    },
    batteryWarranty: useRouteWithWorldId(useBaseWorldRoutes().batteryWarranty),
    bulkActions: useRouteWithWorldId(useBaseWorldRoutes().bulkActions),
    core: {
      overview: useRouteWithWorldId(useBaseWorldRoutes().core.overview),
      devices: useRouteWithWorldId(useBaseWorldRoutes().core.devices),
      devicesList: useRouteWithWorldId(useBaseWorldRoutes().core.devicesList),
      devicesMap: useRouteWithWorldId(useBaseWorldRoutes().core.devicesMap),
      deviceOverview: useRouteWithWorldId(useBaseWorldRoutes().core.deviceOverview),
      devicePerformance: useRouteWithWorldId(useBaseWorldRoutes().core.devicePerformance),
      performance: useRouteWithWorldId(useBaseWorldRoutes().core.performance),
      performanceDevices: useRouteWithWorldId(useBaseWorldRoutes().core.performanceDevices),
      anomalies: useRouteWithWorldId(useBaseWorldRoutes().core.anomalies),
      anomalyHistory: useRouteWithWorldId(useBaseWorldRoutes().core.anomalyHistory),
      anomalyDevices: useRouteWithWorldId(useBaseWorldRoutes().core.anomalyDevices),
      applicationsList: useRouteWithWorldId(useBaseWorldRoutes().core.applicationsList),
      applicationPerformance: useRouteWithWorldId(useBaseWorldRoutes().core.applicationPerformance),
      applicationDevicesList: useRouteWithWorldId(useBaseWorldRoutes().core.applicationDevicesList),
      maps: useRouteWithWorldId(useBaseWorldRoutes().core.maps),
      processesList: useRouteWithWorldId(useBaseWorldRoutes().core.processesList),
      processesCrashes: useRouteWithWorldId(useBaseWorldRoutes().core.processesCrashes),
      processPerformance: useRouteWithWorldId(useBaseWorldRoutes().core.processPerformance),
      accessPoints: useRouteWithWorldId(useBaseWorldRoutes().core.accessPoints),
      accessPointPerformance: useRouteWithWorldId(useBaseWorldRoutes().core.accessPointPerformance),
      anomalyDetectionBeta: useRouteWithWorldId(useBaseWorldRoutes().core.anomalyDetectionBeta),
      deviceAnomalyDetails: useRouteWithWorldId(useBaseWorldRoutes().core.deviceAnomalyDetails),
      deviceAnomalyExamination: useRouteWithWorldId(useBaseWorldRoutes().core.deviceAnomalyExamination),
      batteries: useRouteWithWorldId(useBaseWorldRoutes().core.batteries),
      batteriesList: useRouteWithWorldId(useBaseWorldRoutes().core.batteriesList),
      batteriesDevices: useRouteWithWorldId(useBaseWorldRoutes().core.batteriesDevices)
    },
    settings: {
      basePath: useRouteWithWorldId(useBaseWorldRoutes().settings.basePath),
      alarms: useRouteWithWorldId(useBaseWorldRoutes().settings.alarms),
      alerts: useRouteWithWorldId(useBaseWorldRoutes().settings.alerts),
      anomalies: useRouteWithWorldId(useBaseWorldRoutes().settings.anomalies),
      devices: useRouteWithWorldId(useBaseWorldRoutes().settings.devices),
      platform: useRouteWithWorldId(useBaseWorldRoutes().settings.platform)
    },
    userManagement: useRouteWithWorldId(useBaseWorldRoutes().userManagement),
    homeLocationManagement: useRouteWithWorldId(useBaseWorldRoutes().homeLocationManagement),
    zones: {
      list: useRouteWithWorldId(useBaseWorldRoutes().zones.list),
      new: useRouteWithWorldId(useBaseWorldRoutes().zones.new),
      edit: useRouteWithWorldId(useBaseWorldRoutes().zones.edit)
    },
    enrollment: useRouteWithWorldId(useBaseWorldRoutes().enrollment)
  };
};
